import {
    Avatar,
    Box,
    Button,
    Card,
    CardContent,
    CardHeader,
    Chip,
    Divider,
    FormControl,
    InputLabel,
    MenuItem,
    Select,
    Tab,
    Tabs,
    TextField,
    Typography,
    IconButton
} from '@mui/material';
import { Grid } from '@mui/material';
import {
    Calendar,
    Edit,
    Mail,
    MapPin,
    MessageSquare,
    Package,
    Phone,
    Save,
    User,
    X,
    Camera
} from 'lucide-react';
import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import api from "../../hooks/api";
import { pathAPI } from "../../utils/pathApi";
import { useCountries } from '../../hooks/useCountries';
import { useTranslation } from "react-i18next";
import UserAppLoyaltyTab from "./UserAppLoyaltyTab";
import UserAppOrdersTab from "./UserAppOrdersTab";
import UserAppTicketsTab from "./UserAppTicketsTab";
import { TabPanel } from "./userAppShared";
import { formatPoints } from "./userLoyalty";
import { useUserLoyalty } from "./useUserLoyalty";
import { useBreadcrumbLabel } from "../../components/breadcrumb/BreadcrumbContext";

type SupportedCountry = {
  id: number;
  code: string;
  name: string;
  country_short_code: string;
  currency: string;
  phone_code: string;
  zone_id: string;
  flag: string;
  language_id: number;
  value: number;
};

type UserStatus = 'ACTIVE' | 'INACTIVE' | 'SUSPENDED' | 'PENDING' | 'DEACTIVATE';

type AppUser = {
  id: number;
  full_name: string;
  email: string;
  phone_number?: string;
  country?: SupportedCountry;
  created_date?: string;
  total_orders?: number;
  total_order_values?: number;
  total_supported_tickets?: number;
  list_order?: any[];
  support_tickets?: any[];
  status: UserStatus;
  avatar?: string;
};

type AppUserUpdateRequest = {
  fullName: string;
  avatar?: File;
  country?: SupportedCountry;
  status: UserStatus;
};

export const UserAppDetail = () => {
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const [userData, setUserData] = useState<AppUser | null>(null);
  useBreadcrumbLabel(userData?.full_name || userData?.email);
  const [loading, setLoading] = useState(false);
  const [hasError, setHasError] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [{ data: countries }] = useCountries();
  const [editedData, setEditedData] = useState<AppUser>({
    id: 0,
    full_name: '',
    email: '',
    phone_number: '',
    country: { id: 0, code: '', name: '', country_short_code: '', currency: '', phone_code: '', zone_id: '', flag: '', language_id: 0, value: 0 },
    created_date: '',
    total_orders: 0,
    total_order_values: 0,
    total_supported_tickets: 0,
    list_order: [],
    support_tickets: [],
    status: 'ACTIVE',
    avatar: ''
  });
  const [avatarFile, setAvatarFile] = useState<File | null>(null);
  const [tabValue, setTabValue] = useState(0);
  const loyalty = useUserLoyalty(id);

  const userStatuses: UserStatus[] = ['ACTIVE', 'INACTIVE', 'SUSPENDED', 'PENDING', 'DEACTIVATE'];

  const displayData = isEditing ? editedData : userData;

  const handleEdit = () => {
    setEditedData(userData || editedData);
    setAvatarFile(null);
    setIsEditing(true);
  };

  const handleSave = async () => {
    if (!id) return;
    
    try {
      setLoading(true);
      
      const updateData: AppUserUpdateRequest = {
        fullName: editedData.full_name,
        country: editedData.country,
        status: editedData.status
      };

      if (avatarFile) {
        updateData.avatar = avatarFile;
      }

      const formData = new FormData();
      formData.append('fullName', updateData.fullName);
      if (updateData.country) {
        formData.append('country', JSON.stringify(updateData.country));
      }
      formData.append('status', updateData.status);
      if (updateData.avatar) {
        formData.append('avatar', updateData.avatar);
      }

      const response = await api.PUT(`${pathAPI.appUsers}/${id}`, formData, {}, true);
      
      if (response?.data) {
        setUserData(response.data);
        setEditedData(response.data);
        setIsEditing(false);
        setAvatarFile(null);
      }
    } catch (error) {
      console.error('Error updating user:', error);
      setHasError(true);
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    setEditedData(userData || editedData);
    setAvatarFile(null);
    setIsEditing(false);
  };

  const handleInputChange = (field: keyof AppUser, value: any) => {
    setEditedData(prev => ({ ...prev, [field]: value }));
  };

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  const getPriorityColor = (priority: string) => {
    switch (priority.toLowerCase()) {
      case 'high':
        return '#ef4444';
      case 'medium':
        return '#f59e0b';
      case 'low':
        return '#10b981';
      default:
        return '#6b7280';
    }
  };

  const handleAvatarChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      setAvatarFile(file);
      const reader = new FileReader();
      reader.onload = (e) => {
        const result = e.target?.result as string;
        handleInputChange('avatar', result);
      };
      reader.readAsDataURL(file);
    }
  };

  useEffect(() => {
    const fetchDetail = async () => {
      if (!id) return;
      setLoading(true);
      try {
        const response = await api.GET<{ data: AppUser }>(`${pathAPI.appUsers}/${id}`, {}, {}, true);
        if (response?.data) {
          setUserData(response.data);
          setEditedData(response.data);
        }
      } catch (error) {
        console.error('Error fetching user details:', error);
        setHasError(true);
      } finally {
        setLoading(false);
      }
    };

    fetchDetail();
  }, [id]);


  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <Typography>{t("user_app.detail.loading")}</Typography>
      </Box>
    );
  }

  if (hasError) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <Typography color="error">{t("user_app.detail.load_error")}</Typography>
      </Box>
    );
  }

  return (
    <Card sx={{ margin: '0 auto', p: 3 }}>
      <Box>
        {/* Header */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Box sx={{ position: 'relative' }}>
              <Avatar
                sx={{ width: 80, height: 80 }}
                src={displayData?.avatar}
              >
                {!displayData?.avatar && <User size={40} />}
              </Avatar>
              {isEditing && (
                <IconButton
                  component="label"
                  sx={{
                    position: 'absolute',
                    bottom: 0,
                    right: 0,
                    backgroundColor: '#ba2156',
                    color: 'white',
                    '&:hover': {
                      backgroundColor: '#ba2156',
                      opacity: 0.8
                    },
                    width: 32,
                    height: 32
                  }}
                >
                  <Camera size={16} />
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleAvatarChange}
                    style={{ display: 'none' }}
                  />
                </IconButton>
              )}
            </Box>
            <Box>
              <Typography variant="h4" sx={{ fontWeight: 600, mb: 1 }}>
                {displayData?.full_name || 'User Name'}
              </Typography>
              <Typography variant="body1" color="text.secondary" sx={{ mb: 0.5 }}>
                {displayData?.email || 'email@example.com'}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {t("user_app.detail.user_id", { id: displayData?.id })}
              </Typography>
            </Box>
          </Box>
          <Box sx={{ display: 'flex', gap: 1 }}>
            {!isEditing ? (
              <Button
                variant="contained"
                startIcon={<Edit size={16} />}
                onClick={handleEdit}
                sx={{
                    background: "#ba2156",
                    textTransform:"capitalize",
                    color: "#fff",
                    "&:hover": {
                        background: "#ba2156",
                        color: "#fff",
                    }
                }}
              >
                {t("user_app.detail.edit_profile")}
              </Button>
            ) : (
              <>
                <Button
                  variant="outlined"
                  startIcon={<X size={16} />}
                  onClick={handleCancel}
                  sx={{
                    textTransform:"capitalize",
                  }}
                >
                  {t("btn.cancel")}
                </Button>
                <Button
                  variant="contained"
                  startIcon={<Save size={16} />}
                  onClick={handleSave}
                  disabled={loading}
                  sx={{
                    textTransform:"capitalize",
                  }}
                >
                  {loading ? t("user_app.detail.saving") : t("user_app.detail.save_changes")}
                </Button>
              </>
            )}
          </Box>
        </Box>

        {/* Main Content */}
        <Grid container spacing={3} sx={{ mb: 3 }}>
          {/* Personal Information */}
          <Grid size={{ xs: 12, lg: 6 }}>
            <Card sx={{ height: '100%' }}>
              <CardHeader
                title={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <User size={20} />
                    <Typography variant="h6">{t("user_app.personal.title")}</Typography>
                  </Box>
                }
              />
              <CardContent>
                <Grid container spacing={2}>
                  <Grid size={{ xs: 6 }}>
                    {isEditing ? (
                      <TextField
                        fullWidth
                        label={t("user_app.personal.full_name")}
                        value={editedData.full_name}
                        onChange={(e) => handleInputChange('full_name', e.target.value)}
                        variant="outlined"
                        size="small"
                        inputProps={{ maxLength: 255 }}
                        helperText={t("user_app.personal.chars", { length: editedData.full_name.length })}
                      />
                    ) : (
                      <Box>
                        <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                          {t("user_app.personal.full_name")}
                        </Typography>
                        <Typography variant="body1">{displayData?.full_name || t("user_app.na")}</Typography>
                      </Box>
                    )}
                  </Grid>

                  <Grid size={{ xs: 6 }}>
                    {isEditing ? (
                      <TextField
                        fullWidth
                        label={t("user_app.personal.email")}
                        type="email"
                        value={editedData.email}
                        disabled
                        variant="outlined"
                        size="small"
                        helperText={t("user_app.personal.email_readonly")}
                      />
                    ) : (
                      <Box>
                        <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                          {t("user_app.personal.email")}
                        </Typography>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Mail size={16} />
                          <Typography variant="body1">{displayData?.email || t("user_app.na")}</Typography>
                        </Box>
                      </Box>
                    )}
                  </Grid>

                  <Grid size={{ xs: 6 }}>
                    {isEditing ? (
                      <TextField
                        fullWidth
                        label={t("user_app.personal.phone")}
                        value={editedData.phone_number}
                        disabled
                        variant="outlined"
                        size="small"
                        helperText={t("user_app.personal.phone_readonly")}
                      />
                    ) : (
                      <Box>
                        <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                          {t("user_app.personal.phone")}
                        </Typography>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Phone size={16} />
                          <Typography variant="body1">{displayData?.phone_number || t("user_app.na")}</Typography>
                        </Box>
                      </Box>
                    )}
                  </Grid>

                  <Grid size={{ xs: 6 }}>
                    {isEditing ? (
                      <FormControl fullWidth size="small">
                        <InputLabel>{t("user_app.personal.country")}</InputLabel>
                        <Select
                          value={editedData.country?.id || ''}
                          label={t("user_app.personal.country")}
                          onChange={(e) => {
                            const selectedCountry = countries.find(c => c.id === e.target.value);
                            handleInputChange('country', selectedCountry);
                          }}
                        >
                          {countries.map((country) => (
                            <MenuItem key={country.id} value={country.id}>
                              {country.name}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    ) : (
                      <Box>
                        <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                          {t("user_app.personal.country")}
                        </Typography>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <MapPin size={16} />
                          <Typography variant="body1">{displayData?.country?.name || t("user_app.na")}</Typography>
                        </Box>
                      </Box>
                    )}
                  </Grid>

                  <Grid size={{ xs: 6 }}>
                    {isEditing ? (
                      <FormControl fullWidth size="small">
                        <InputLabel>{t("user_app.personal.status")}</InputLabel>
                        <Select
                          value={editedData.status}
                          label={t("user_app.personal.status")}
                          onChange={(e) => handleInputChange('status', e.target.value)}
                        >
                          {userStatuses.map((status) => (
                            <MenuItem key={status} value={status}>
                              {t(`user_app.status.${status}`, { defaultValue: status })}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    ) : (
                      <Box>
                        <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                          {t("user_app.personal.status")}
                        </Typography>
                        <Chip
                          label={displayData?.status ? t(`user_app.status.${displayData.status}`, { defaultValue: displayData.status }) : t("user_app.na")}
                          size="small"
                        />
                      </Box>
                    )}
                  </Grid>

                  <Grid size={{ xs: 6 }}>
                    <Box>
                      <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                        {t("user_app.personal.created_date")}
                      </Typography>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Calendar size={16} />
                        <Typography variant="body1">
                          {displayData?.created_date ? new Date(displayData.created_date).toLocaleDateString() : t("user_app.na")}
                        </Typography>
                      </Box>
                    </Box>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>

          {/* Activity Summary */}
          <Grid size={{ xs: 12, lg: 6 }}>
            <Card sx={{ height: '100%' }}>
              <CardHeader title={<Typography variant="h6">{t("user_app.summary.title")}</Typography>} />
              <CardContent>
                <Grid container spacing={2} sx={{ mb: 2 }}>
                  <Grid size={{ xs: 6 }}>
                    <Box
                      sx={{
                        textAlign: 'center',
                        p: 2,
                        backgroundColor: 'rgba(37, 99, 235, 0.08)',
                        borderRadius: 1
                      }}
                    >
                      <Package size={32} color="#2563eb" style={{ margin: '0 auto 8px' }} />
                      <Typography variant="h4" sx={{ color: '#2563eb', fontWeight: 600 }}>
                        {displayData?.list_order?.length || 0}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {t("user_app.summary.total_orders")}
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid size={{ xs: 6 }}>
                    <Box
                      sx={{
                        textAlign: 'center',
                        p: 2,
                        backgroundColor: 'rgba(22, 163, 74, 0.08)',
                        borderRadius: 1
                      }}
                    >
                      <MessageSquare size={32} color="#16a34a" style={{ margin: '0 auto 8px' }} />
                      <Typography variant="h4" sx={{ color: '#16a34a', fontWeight: 600 }}>
                        {displayData?.support_tickets?.filter(ticket => ticket.status === 'WAITING').length || 0}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {t("user_app.summary.open_tickets")}
                      </Typography>
                    </Box>
                  </Grid>
                </Grid>
                <Grid container spacing={2}>
                  <Grid size={{ xs: 6 }}>
                    <Box
                      sx={{
                        textAlign: 'center',
                        p: 2,
                        backgroundColor: 'rgba(217, 119, 6, 0.08)',
                        borderRadius: 1
                      }}
                    >
                      <Typography variant="h4" sx={{ color: '#d97706', fontWeight: 600 }}>
                        ${displayData?.total_order_values?.toFixed(2) || '0.00'}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {t("user_app.summary.total_value")}
                      </Typography>
                    </Box>
                  </Grid>
                    <Grid size={{ xs: 6 }}>
                    <Box
                      sx={{
                        textAlign: 'center',
                        p: 2,
                        backgroundColor: 'rgba(190, 24, 93, 0.08)',
                        borderRadius: 1
                      }}
                    >
                      <Typography variant="h4" sx={{ color: '#be185d', fontWeight: 600 }}>
                        {displayData?.total_supported_tickets || 0}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {t("user_app.summary.total_tickets")}
                      </Typography>
                    </Box>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* Tabs */}
        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tabs value={tabValue} onChange={handleTabChange}>
            <Tab label={t("user_app.tabs.orders", { n: displayData?.list_order?.length || 0 })} />
            <Tab label={t("user_app.tabs.tickets", { n: displayData?.support_tickets?.filter(ticket => ticket.status === 'WAITING').length || 0 })} />
            <Tab label={t("user_app.tabs.loyalty", { n: formatPoints(loyalty.summary.availablePoints) })} />
          </Tabs>
        </Box>

        {/* Orders Tab */}
        <TabPanel value={tabValue} index={0}>
          <UserAppOrdersTab orders={displayData?.list_order} />
        </TabPanel>

        {/* Support Tickets Tab */}
        <TabPanel value={tabValue} index={1}>
          <UserAppTicketsTab tickets={displayData?.support_tickets} />
        </TabPanel>

        {/* Loyalty Points Tab */}
        <TabPanel value={tabValue} index={2}>
          <UserAppLoyaltyTab loyalty={loyalty} />
        </TabPanel>
      </Box>
    </Card>
  );
};
